import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './managed-apps-select.scss';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../store';
import CustomButton from '../../../shared/custom-button/custom-button';
import { Input, Modal } from 'antd';
import { debounce } from 'lodash';
import SearchIcon from '@svg/search.svg?react';
import ClearIcon from '@svg/x-icon.svg?react';
import { ListItem } from '../../../shared/entity-selector/entity-selector';
import { AppDto } from '../../../../dtos/app.dto';
import { ResultsContainer } from './results-container';
import { IconType } from '../../../shared/custom-icon/custom-icon';
import { EmptyState } from '../../../shared/empty-state/empty-state';
import { labelsDisplayCalc } from '../../../../utils/labelsDisplayUtil';
import { SelectedAppsLabel } from '../selected-apps-lables';
import { TryAgainMessage } from 'src/components/shared/try-again-message/try-again-message';

interface ManagedAppsSelectModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    selectedAppIds: Record<string, string>;
    alertMessage: string;
    saveApps: (selectedList: ListItem[]) => void;
    className?: string;
    shouldDisplayAlert: boolean;
    emptyStateMessage: string;
}

export const ManagedAppsSelectModal: React.FC<ManagedAppsSelectModalProps> = observer(({ isOpen, setIsOpen, selectedAppIds, alertMessage, shouldDisplayAlert,
    saveApps, className, emptyStateMessage }) => {
    const { fwVersionStore } = useStores();
    const [selectedList, setSelectedList] = useState<ListItem[]>([]);
    const [appsMiddleware, setAppsMiddleware] = useState<{ result: AppDto[], searchText: string }>({ result: [], searchText: '' });
    const [apps, setApps] = useState<AppDto[]>([]);
    const [searchText, setSearchText] = useState('');
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const minCharsForSearch = 2;

    useEffect(() => {
        if (!isOpen) {
            closeModal();
        }
    }, [isOpen]);

    useEffect(() => {
        if (searchText === appsMiddleware.searchText) {
            if (searchText.length > 1)
                setApps(appsMiddleware.result);
            setIsSearchLoading(false);
        }
    }, [appsMiddleware, searchText]);

    const debouncedSearch = useMemo(() => debounce(async (searchString: string) => {
        try {
            const apps = await fwVersionStore.searchApps(searchString);
            const noExistingApps = apps.filter(({ applicationMetadata }) => !selectedAppIds[applicationMetadata.id]);
            setAppsMiddleware({ result: noExistingApps, searchText: searchString });
        } catch {
            setApps([]);
            setIsError(true);
            setIsSearchLoading(false);
        }
    }, 500), [selectedAppIds]);

    const options = useMemo(() =>
        apps.map(app => ({ label: app.applicationMetadata.displayName, value: app.applicationMetadata.id, dep: app.dependencies })),
        [apps]);

    useEffect(() => {
        setIsError(false);
        if (searchText.length >= minCharsForSearch) {
            setIsSearchLoading(true);
            void debouncedSearch(searchText);
        }
    }, [searchText]);

    const labels: ListItem[] = useMemo(() => {
        const labelsWidth = 800 - 4 * 16; // 736px
        const labelPadding = 2 * 12; // 24px
        const removeIconWidth = 10;
        const labelGap = 8;
        const avgCharacterWidth = 8;

        return labelsDisplayCalc(labelsWidth, labelPadding, removeIconWidth, labelGap, avgCharacterWidth, selectedList);
    }, [selectedList]);

    const remove = (id: string) => {
        setSelectedList(selectedList.filter(item => item.key !== id));
    };

    const add = (item: ListItem) => {
        setSelectedList([...selectedList, item]);
    };

    const onChange = (checked: boolean, item: ListItem) => {
        if (checked) {
            add(item);
        }
        else {
            remove(item.key);
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const newSelectedOptionsToAdd = options
                .filter(option => !selectedList.some(b => b.key === option.value))
                .map(option => ({ name: option.label, key: option.value, data: { dep: option.dep } }));
            setSelectedList([...selectedList, ...newSelectedOptionsToAdd]);
        }
        else {
            const selectedOptionsAfterUnselectAll = selectedList.filter(item => !options.some(option => option.value === item.key));
            setSelectedList(selectedOptionsAfterUnselectAll);
        }
    };

    const closeModal = () => {
        setIsLoading(false);
        setIsOpen(false);
        setSelectedList([]);
        setApps([]);
        setIsError(false);
        setSearchText('');
    };

    const addSelectedApps = () => {
        setIsLoading(true);
        saveApps(selectedList);
    };
    const result = useCallback(() => {
        if (searchText.length < minCharsForSearch)
            return <EmptyState iconType={IconType.APPS_EMPTY_STATE}
                subTitle={emptyStateMessage} />;
        return (isError && !isSearchLoading) ? <TryAgainMessage /> :
            <ResultsContainer options={options} selectedList={selectedList} isSearchLoading={isSearchLoading}
                onChange={onChange} handleSelectAll={handleSelectAll} alertMessage={alertMessage} shouldDisplayAlert={shouldDisplayAlert} />;
    }, [searchText, isError, isSearchLoading, selectedList]);
    
    return <Modal mask={false}
        open={isOpen}
        footer={null}
        wrapClassName={`managed-apps-popover ${className}`}
        width={800}
        onCancel={closeModal}>
        <div className={'managed-apps-select'}>
            <div className={'header'}>Add Apps</div>
            <div className={'content'}>
                <div className={'search-box'}>
                    <Input
                        type="text"
                        className={'search-input'}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="Search..."
                        prefix={<SearchIcon />}
                        suffix={<button type="button" className="clear-btn" aria-label="Clear search"
                                        onClick={() => setSearchText('')}><ClearIcon /></button>}
                    />
                    {!!labels.length && <SelectedAppsLabel selectedList={selectedList} labels={labels} remove={remove} />}
                </div>
                {result()}
            </div>
            <div className={'footer'}>
                <span className={'selected-count'}>{selectedList.length} selected</span>
                <CustomButton label={'Cancel'} className={'secondary-btn btn-cancel'} onClick={closeModal} />
                <CustomButton label={'Add'} className={`primary-btn btn-add ${isLoading ? 'loading' : ''}`}
                    isDisabled={selectedList.length === 0 || isLoading} onClick={addSelectedApps} />
            </div>
        </div>
    </Modal>;
});